import { useState } from 'react';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';

import {
  FileReportSendResponseViewModel,
  FileReportUploadResponse,
  ReportingReceiptViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import {
  Assets,
  Box,
  Button,
  Container,
  DownloadButton,
  FAQ,
  Feedback,
  Flex,
  Headings,
  Line,
  Link,
  Panel,
  QuestionsAndAnswersType,
  scrollToTop,
  Text,
  theme,
  useMediaMin,
  VerticalSpacer,
} from '@foradevelopment/fora-ui';
import { distinct } from '@fora-monorepo/list-utils';

import { ReportModal } from '../Report/ReportModal';
import { useAppSelector } from '../../redux/hooks';
import { useTypedTranslation } from '../../i18n/i18n';
import { FileReportTempTier1ErrorMessage } from './FileReportTempTier1ErrorMessage';

interface FileReportingReceiptPanelProps {
  devLogging?: boolean;
  faqs: QuestionsAndAnswersType[];
  fileResponse: FileReportUploadResponse | null;
  receiptResponse: FileReportSendResponseViewModel | null;
  getReportSummaryModalData: (index: number) => Promise<ReportingReceiptViewModel>;
  downloadPayrollDetailsCSV: (
    fileReportUploadUid: string,
    reportIndex: number,
    organizationNumber: string,
    employerName: string
  ) => Promise<string | null>;
  resetFileReporting: () => void;
}

export const FileReportingReceiptPanel = ({
  devLogging,
  faqs,
  fileResponse,
  receiptResponse,
  getReportSummaryModalData,
  downloadPayrollDetailsCSV,
  resetFileReporting,
}: FileReportingReceiptPanelProps) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const { t } = useTypedTranslation();
  const { receiptPanelState } = useAppSelector(state => state.file);

  const uniquePaymonths = fileResponse?.fileReportSummaries?.reduce((payMonths, reportSummary, index) => {
    const newMonths = [] as string[];
    if (receiptResponse && receiptResponse[index].success) {
      reportSummary.reportPeriods?.forEach(paymonth => {
        if (!payMonths.includes(paymonth.payMonth)) newMonths.push(paymonth.payMonth);
      });
    }
    return [...payMonths, ...newMonths];
  }, [] as string[]);
  const uniquePaymonthStrings = uniquePaymonths?.map(paymonth => getReadablePayMonth(paymonth));

  const { ffSurveyMultiReporter } = useAppSelector(state => state.foraGlobal.settings);

  const hasEmploymentData = !!fileResponse?.fileReportSummaries?.some(sum => sum.totalEmploymentsReported);
  const hasPayrollData = !!fileResponse?.fileReportSummaries?.some(sum => sum.totalPayrollsReported);

  const resetAndGoToOverview = () => {
    resetFileReporting();
    scrollToTop({ smooth: true });
  };

  return (
    <Panel {...receiptPanelState} previousPanelName="panel-review-confirm" name="panel-receipt">
      <Panel.Header>{t('common.receiptTitle')}</Panel.Header>
      <Panel.Body>
        <Panel.MainSection>
          <Container maxWidth={600}>
            {
              <Text size="medium" fontWeight="bold" color="black70" marginBottom={20} marginTop={20}>
                {t('common.organizationNameAndNumber', {
                  organizationName:
                    fileResponse && fileResponse.fileReportSummaries
                      ? distinct(fileResponse?.fileReportSummaries.map(x => x.organizationName) ?? [])
                      : '',
                  orgNumber:
                    fileResponse && fileResponse.fileReportSummaries
                      ? distinct(fileResponse?.fileReportSummaries.map(x => x.organizationNumber) ?? [])
                      : '',
                })}
              </Text>
            }
            <Assets.CheckmarkIcon color="black90" width={isDesktop ? 30 : 18} marginBottom={14} />
            <Headings.H2 marginBottom={40}> {t('panel.manualReportReceipt.thankYou')}!</Headings.H2>
            <Text fontWeight="light" size="xs" color="steelGrey">
              {t('panel.fileReportReceipt.reportsTitle')}
            </Text>
            {uniquePaymonthStrings?.map((uniquePaymonthString, i) => (
              <Text key={i} fontWeight="light" size="medium">
                {t('panel.fileReportReceipt.receiptSalary', { payMonth: uniquePaymonthString })}
              </Text>
            ))}
            {hasEmploymentData && (
              <Text fontWeight="light" size="medium">
                {t('panel.reviewAndConfirm.employeeInfoTitle')}
              </Text>
            )}
            <VerticalSpacer marginBottom="1.25rem" />
            {fileResponse?.fileReportSummaries?.map((reportSummary, reportIndex) => (
              <Flex key={reportIndex} marginBottom={5} flexDirection="column" alignItems="flex-start">
                <Text fontWeight="bold" size="large">
                  {t('common.downloadReceiptTitle')}
                  {fileResponse?.fileReportSummaries?.length === 1
                    ? ''
                    : ' för rapport ' + (reportSummary.refId || reportIndex + 1)}
                </Text>
                {receiptResponse?.at(reportIndex)?.success && (
                  <>
                    <ReportModalAndButton
                      reportIndex={reportIndex}
                      getReportSummaryModalData={getReportSummaryModalData}
                    />
                    {reportSummary.totalPayrollsReported > 0 && (
                      <DownloadButton
                        name={t('list.historyLog.reportedPayrollCsvFileName')}
                        download={() =>
                          downloadPayrollDetailsCSV(
                            fileResponse.fileReportUploadUID,
                            reportIndex,
                            reportSummary.organizationNumber,
                            reportSummary.organizationName
                          )
                        }
                      />
                    )}
                  </>
                )}
                {!receiptResponse?.at(reportIndex)?.success && (
                  <Feedback type="error" marginBottom={10} iconAbove>
                    {receiptResponse?.at(reportIndex)?.friendlyErrorMessage ? (
                      <FileReportTempTier1ErrorMessage />
                    ) : (
                      <Text size="small">{t('panel.fileReportReceipt.submitReportFailedText')}</Text>
                    )}
                    {devLogging && (
                      <Text size="small" marginTop={10}>
                        S4I error message: {receiptResponse?.at(reportIndex)?.errorMessage}
                      </Text>
                    )}
                  </Feedback>
                )}
              </Flex>
            ))}
            <Box marginTop={60}>
              <Headings.H3 marginBottom={10}> {t('panel.fileReportReceipt.receiptInfoHeading')}</Headings.H3>
              {(hasPayrollData || hasEmploymentData) && (
                <Text size="medium">{t('panel.fileReportReceipt.receiptInfoText')}</Text>
              )}
            </Box>

            <VerticalSpacer marginBottom={60} />
            <Line marginBottom={ffSurveyMultiReporter ? 30 : 60} color="black10" />
            {ffSurveyMultiReporter && <Link to="https://www.fora.se/survey-e-service-employer">Lämna ditt omdöme</Link>}
            <Flex columnGap={20} rowGap={20} flexWrap="wrap" marginTop={ffSurveyMultiReporter ? 30 : 0}>
              <Button style={{ maxWidth: 296 }} variant="outline" onClick={resetAndGoToOverview}>
                {t('panel.fileReportReceipt.continueButtonText')}
              </Button>
            </Flex>
          </Container>
        </Panel.MainSection>

        <Panel.AsideSection>{renderAsideSection(faqs)}</Panel.AsideSection>
      </Panel.Body>
    </Panel>
  );
};

const renderAsideSection = (faqs: QuestionsAndAnswersType[]) => (
  <FAQ testId="test-storybook-faq" layout="side" questionAnswers={faqs} />
);

const ReportModalAndButton = ({
  reportIndex,
  getReportSummaryModalData,
}: {
  reportIndex: number;
  getReportSummaryModalData: (index: number) => Promise<ReportingReceiptViewModel>;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTypedTranslation();
  const appInsights = useAppInsightsContext();
  const name = t('common.reportModalContentLabel');
  const trackOpenModal = useTrackEvent(appInsights, 'File reporting - opened modal', { name }, true);

  return (
    <>
      <Button
        styleAs="link"
        onClick={() => {
          setModalOpen(true);
          trackOpenModal({ name });
        }}
      >
        <Assets.FileIcon width={10} marginRight={5} containerSize={12} />
        {name}
      </Button>
      <ReportModal
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        getReportData={() => getReportSummaryModalData(reportIndex)}
      />
    </>
  );
};
